import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { getTeams, deleteTeam, type Team, TEAM_VISIBILITY } from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';
import CollaborationNav from './CollaborationNav';

export default function TeamListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [visibilityFilter, setVisibilityFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const canRead = hasPermission('collaboration', 'read');
  const canWrite = hasPermission('collaboration', 'write');
  const canDelete = hasPermission('collaboration', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: Record<string, any> = { ...params };
      if (visibilityFilter) filters.visibility = visibilityFilter;
      if (activeFilter) filters.is_active = activeFilter;
      return getTeams(filters);
    },
    [visibilityFilter, activeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('collaboration.team.delete_confirm'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteTeam(id);
            message.success(t('collaboration.team.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Team> = useMemo(
    () => [
      {
        title: t('collaboration.team.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('collaboration.team.field.description'),
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
      },
      {
        title: t('collaboration.team.field.owner'),
        dataIndex: 'owner_id',
        key: 'owner_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('collaboration.team.field.members'),
        dataIndex: 'members',
        key: 'members',
        width: 100,
        render: (members: any[]) => members?.length ?? 0,
      },
      {
        title: t('collaboration.team.field.visibility'),
        dataIndex: 'visibility',
        key: 'visibility',
        width: 100,
        render: (v: string) => (
          <Tag color={v === 'public' ? 'green' : 'default'}>
            {t(`collaboration.team.visibility_options.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('collaboration.team.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'red'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('common.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 140,
        render: (_: unknown, record: Team) => (
          <Space>
            {canRead && (
              <Tooltip title={t('app.view')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/collaboration/teams/${record.id}`)}
                />
              </Tooltip>
            )}
            {canWrite && (
              <Tooltip title={t('app.edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/collaboration/teams/${record.id}/edit`)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('app.delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canRead, canWrite, canDelete, navigate, handleDelete]
  );

  return (
    <div>
      <CollaborationNav />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('collaboration.team.field.visibility')}
          style={{ width: 140 }}
          value={visibilityFilter}
          onChange={setVisibilityFilter}
          options={TEAM_VISIBILITY.map((v) => ({
            value: v,
            label: t(`collaboration.team.visibility_options.${v}`),
          }))}
        />
        <Select
          allowClear
          placeholder={t('collaboration.team.field.is_active')}
          style={{ width: 120 }}
          value={activeFilter}
          onChange={setActiveFilter}
          options={[
            { value: 'true', label: t('app.yes') },
            { value: 'false', label: t('app.no') },
          ]}
        />
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/collaboration/teams/new')}
          >
            {t('collaboration.team.new')}
          </Button>
        )}
      </Space>
      <DataGrid
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />
    </div>
  );
}
